import React, { useContext, useState, useMemo } from "react";
import {
  ITravelOfferCardContent,
  TravelWalletSingleSelectionWorkflow,
  TravelWalletSingleSelectionStep,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  getTravelOfferCardContent,
  LoadingIndicator,
  B2BSpinner,
  getTravelCreditCardContent,
  getTravelCreditHistoryModalProps,
  IconName,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
} from "halifax";
import {
  CONTINUE,
  CREDITS_CTA,
  getOffersModalTitle,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  LOADING_OFFERS,
  LOADING_WALLET,
  MODAL_HOTELS_HEADER_TEXT_CREDITS,
  VIEW_OFFER_DETAILS_CTA_COPY,
} from "./textConstants";
import { TravelOfferSelectionConnectorProps } from "./container";
import { RouteComponentProps } from "react-router-dom";
import "./styles.scss";
import { Box } from "@material-ui/core";
import {
  CallState,
  Funnel,
  SelectedTravelOfferScreen,
  SELECTED_TRAVEL_OFFER,
  VIEWED_OFFER_FAQ,
  TravelWalletOffer,
  TravelWalletCredit,
} from "redmond";
import clsx from "clsx";
import { getConfigTenant, isCorpTenant, hasIneligibleTravelOffer } from "@capone/common";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelOfferSelectionProps
  extends TravelOfferSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  progressStep?: TravelWalletSingleSelectionStep;
  mobileHeaderElement?: JSX.Element;
  onGoBack?: () => void;
  onContinueClick?: () => void;
}

export const TravelOfferSelection = (props: ITravelOfferSelectionProps) => {
  const {
    className,
    offers,
    bestOverallOffer,
    offerToApply,
    setOfferToApply,
    isMobile,
    progressStep,
    mobileHeaderElement,
    onGoBack,
    onContinueClick,
    fetchApplicableTravelWalletItemsCallState,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    showRequoteSuccessBanner,
    isRequotePending,
    credit,
    creditBreakdown,
    creditToApply,
    setCreditToApply,
    maxApplicableTravelWalletCreditOnly,
    isTravelWalletPaymentOnly,
    creditsTransactionHistory,
    allWalletOffers,
    bookingTotal = 0,
  } = props;
  const [travelOfferCardContent, setTravelOfferCardContent] = useState<
    ITravelOfferCardContent | undefined
  >(undefined);
  const [currentSelectedOffer, setCurrentSelectedOffer] = useState<
    TravelWalletOffer | undefined
  >(undefined);
  const [currentSelectedCredit, setCurrentSelectedCredit] = useState<
    TravelWalletCredit | undefined
  >(undefined);
  const [openModal, setOpenModal] = useState(false);

  const expState = useExperiments();
  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const isFtbEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS
    ) === AVAILABLE;

  const firstName =
    useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const isLoading =
    fetchApplicableTravelWalletItemsCallState === CallState.InProcess;

  const modalScreen = SelectedTravelOfferScreen.HOTEL_CHECKOUT;

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers]
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer]
  );

  const hasIneligibleFTB = useMemo(
    () =>
      isFtbEnabled &&
      isCorpTenant(getConfigTenant()) &&
      hasIneligibleTravelOffer(allWalletOffers, filteredOffers, Funnel.Lodging),
    [isFtbEnabled, filteredOffers, allWalletOffers]
  );

  const trackViewedOfferFaq = () => {
    trackEvent({
      eventName: VIEWED_OFFER_FAQ,
      properties: {
        screen: modalScreen,
      },
    });
  };

  React.useEffect(() => {
    switch (progressStep) {
      case TravelWalletSingleSelectionStep.Main:
        setOpenModal(false);
        break;
      case TravelWalletSingleSelectionStep.TravelWalletSelection:
        setOpenModal(true);
        break;
      default:
        break;
    }
  }, [progressStep]);

  React.useEffect(() => {
    const offerAmount = filteredBestOverallOffer?.maxDiscountAmount
      ? filteredBestOverallOffer?.maxDiscountAmount?.amount
      : filteredBestOverallOffer?.amount.amount;
    if (
      (!isTravelWalletCreditsExperiment && filteredBestOverallOffer) ||
      Math.abs(offerAmount || 0) > Math.abs(credit?.amount.amount || 0)
    ) {
      setOfferToApply(filteredBestOverallOffer);
      setCurrentSelectedOffer(filteredBestOverallOffer);
      setCreditToApply(undefined);
      setCurrentSelectedCredit(undefined);
    } else if (credit) {
      setCreditToApply(credit);
      setCurrentSelectedCredit(credit);
      setOfferToApply(undefined);
      setCurrentSelectedOffer(undefined);
    }
  }, [filteredBestOverallOffer, credit]);

  React.useEffect(() => {
    if (currentSelectedOffer) {
      const content = getTravelOfferCardContent(currentSelectedOffer);
      setTravelOfferCardContent(content);
    }
  }, [currentSelectedOffer]);

  React.useEffect(() => {
      const hasRealRewardsSelection =
        selectedRewardsPaymentAccountId &&
        selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

      if (selectedPaymentMethodId || hasRealRewardsSelection) {
        // Don't clear the card across a reshop or connection-retry (CT1261T-419)
        // re-quote — the wallet re-fetch would otherwise wipe it. Guard both
        // windows: isRequotePending (re-quote in flight, banner not set yet) and
        // showRequoteSuccessBanner (post-success), since the wallet response can
        // land in either. Rewards still reset.
        if (!isRequotePending && !showRequoteSuccessBanner) {
          resetPaymentCardSelectedAccounts();
        }
        setSelectedRewardsPaymentTotal("", null, null, null);
      }
      // The requote flags are read above but intentionally not deps: only an
      // offer/credit change should run this. A flag flip (e.g. dismissal)
      // must not re-run it and clear the preserved card.
    }, [offerToApply, creditToApply]);

  const creditDetailsCardProps = React.useMemo(
    () =>
      getTravelCreditCardContent(
        creditBreakdown,
        config.TENANT,
        firstName,
        credit?.amount
      ),
    [creditBreakdown, firstName, credit]
  );

  if ((!filteredOffers || !filteredBestOverallOffer) && !credit && !isLoading && !hasIneligibleFTB) return null;

  return isMobile ? (
    <MobilePopoverCard
      className={clsx("mobile-select-offers-popup", "b2b")}
      open={openModal}
      onClose={() => setOpenModal(false)}
      fullScreen
      headerElement={mobileHeaderElement}
      topLeftButton={
        onGoBack && (
          <BackButton
            className="hotel-offers-popup-back-button"
            onClick={onGoBack}
          />
        )
      }
    >
      <TravelWalletSingleSelectionWorkflow
        travelOfferCardContent={travelOfferCardContent}
        modalCTACopy={
          currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
        }
        modalHeaderText={
          currentSelectedCredit
            ? MODAL_HOTELS_HEADER_TEXT_CREDITS(firstName)
            : undefined
        }
        offers={filteredOffers || []}
        className={className}
        offerToApply={offerToApply}
        setOfferToApply={setOfferToApply}
        currentSelectedOffer={currentSelectedOffer}
        setCurrentSelectedOffer={setCurrentSelectedOffer}
        getOfferDescription={getSelectedOfferDescription}
        modalTitle={getOffersModalTitle(filteredOffers || [], true)}
        isMobile={isMobile}
        onSetOpenModal={(open) => {
          if (open && offerToApply) {
            trackEvent({
              eventName: SELECTED_TRAVEL_OFFER,
              properties: {
                screen: modalScreen,
                offer_count: filteredOffers?.length,
                ...offerToApply?.trackingPropertiesV2?.properties,
              },
              encryptedProperties: [
                offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
              ],
            });
          }
        }}
        onFirstTravelOfferCardChange={trackViewedOfferFaq}
        credit={isTravelWalletCreditsExperiment ? credit : undefined}
        creditToApply={creditToApply}
        setCreditToApply={setCreditToApply}
        currentSelectedCredit={currentSelectedCredit}
        setCurrentSelectedCredit={setCurrentSelectedCredit}
        getCreditDescription={getSelectedCreditDescription}
        creditAmountToApply={maxApplicableTravelWalletCreditOnly}
        creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
        isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
        showCreditHistory={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
        }
        travelCreditHistoryProps={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
            ? getTravelCreditHistoryModalProps(
                firstName,
                creditsTransactionHistory,
                credit
              )
            : undefined
        }
        isTravelSale={isTravelSaleEnabled}
        allWalletOffers={allWalletOffers}
        bookingTotal={bookingTotal}
        currentFunnel={Funnel.Lodging}
      />
      {onContinueClick && (
        <MobileFloatingButton
          className="offers-continue-button"
          onClick={onContinueClick}
          wrapperClassName="b2b"
        >
          {CONTINUE}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  ) : (
    <Box className="checkout-travel-offer-container">
      {isLoading ? (
        <LoadingIndicator
          indicator={B2BSpinner}
          message={
            isTravelWalletCreditsExperiment ? LOADING_WALLET : LOADING_OFFERS
          }
          indicatorSize="medium"
          textAlign="center"
        />
      ) : (
        <>
          <TravelWalletSingleSelectionWorkflow
          travelOfferCardContent={travelOfferCardContent}
          modalCTACopy={
            currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
          }
          modalHeaderText={
            currentSelectedCredit
              ? MODAL_HOTELS_HEADER_TEXT_CREDITS(firstName)
              : undefined
          }
          offers={filteredOffers || []}
          className={className}
          offerToApply={offerToApply}
          setOfferToApply={setOfferToApply}
          currentSelectedOffer={currentSelectedOffer}
          setCurrentSelectedOffer={setCurrentSelectedOffer}
          getOfferDescription={getSelectedOfferDescription}
          modalTitle={getOffersModalTitle(filteredOffers || [], false)}
          isMobile={isMobile}
          onSetOpenModal={(open) => {
            if (open) {
              trackEvent({
                eventName: SELECTED_TRAVEL_OFFER,
                properties: {
                  screen: modalScreen,
                  offer_count: filteredOffers?.length,
                  ...offerToApply?.trackingPropertiesV2?.properties,
                },
                encryptedProperties: [
                  offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
                ],
              });
            }
          }}
          onFirstTravelOfferCardChange={trackViewedOfferFaq}
          credit={isTravelWalletCreditsExperiment ? credit : undefined}
          creditToApply={creditToApply}
          setCreditToApply={setCreditToApply}
          currentSelectedCredit={currentSelectedCredit}
          setCurrentSelectedCredit={setCurrentSelectedCredit}
          getCreditDescription={getSelectedCreditDescription}
          creditAmountToApply={maxApplicableTravelWalletCreditOnly}
          creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
          isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
          showCreditHistory={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
          }
          travelCreditHistoryProps={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
              ? getTravelCreditHistoryModalProps(
                  firstName,
                  creditsTransactionHistory,
                  credit,
                  IconName.PiggyBankCircleOutline
                )
              : undefined
          }
          isTravelSale={isTravelSaleEnabled}
          allWalletOffers={allWalletOffers}
          bookingTotal={bookingTotal}
          currentFunnel={Funnel.Lodging}
        />
        </>
      )}
    </Box>
  );
};
